import fetchLoyaltyAccount from 'actions/loyalty/fetchLoyaltyAccount'
import { API_CALL } from 'actions/actionConstants'
import { FETCH_LOYALTY_ACCOUNT } from 'actions/apiActionConstants'
import requestLoyaltyAccount from 'api/loyalty/requestLoyaltyAccount'
import Group from 'components/utils/Group'
import LoadingBox from 'components/Common/Loading/LoadingBox'
import TextButton from 'LuxKit/Button/TextButton'
import Pane from 'components/Common/Pane'
import Heading from 'LuxKit/Typography/Heading'
import { parseAsString, useQueryState } from 'nuqs'
import { rem } from 'polished'
import { useCallback, useEffect, useMemo, useState } from 'react'
import type { ReactNode } from 'react'
import { FormattedMessage, useIntl } from 'react-intl'
import { useAppDispatch, useAppSelector } from 'hooks/reduxHooks'
import { selectLoggedIn } from 'selectors/accountSelectors'
import styled from 'styled-components'
import useLoyaltyPurchasePointsOffers from 'loyalty/hooks/useLoyaltyPurchasePointsOffers'
import loyaltyPointsPurchasePickBestOffer from 'loyalty/lib/loyaltyPointsPurchasePickBestOffer'
import LoyaltyPurchasePointsBreakdown from './LoyaltyPurchasePointsBreakdown'
import LoyaltyPurchasePointsCapReachedBanner from './LoyaltyPurchasePointsCapReachedBanner'
import LoyaltyPurchasePointsLoggedOutCta from './LoyaltyPurchasePointsLoggedOutCta'
import LoyaltyPurchasePointsNeedsMembershipCta from './LoyaltyPurchasePointsNeedsMembershipCta'
import LoyaltyPurchasePointsOfferSelect from './LoyaltyPurchasePointsOfferSelect'
import getHasLoyaltyAccount from 'loyalty/selectors/getHasLoyaltyAccount'
import BodyText from 'LuxKit/Typography/BodyText'
import getIsCurrentRegionUserLoyaltyRegion from 'loyalty/selectors/getIsCurrentRegionUserLoyaltyRegion'
import CSSBreakpoint from 'components/utils/CSSBreakpoint'
import TextLink from 'LuxKit/TextLink'
import LineSyncIcon from 'LuxKit/Icons/line/LineSyncIcon'
import switchRegion from 'lib/geo/switchRegion'
import getLoyaltyProgramDisplayName from 'loyalty/selectors/getLoyaltyProgramDisplayName'
import getLoyaltyAccountRegion from 'loyalty/selectors/getLoyaltyAccountRegion'
import config from 'constants/config'

const PaneInner = styled.div`
  padding: ${rem(24)};
`

interface Props {
  onContinue: (offerId: string) => void
}

function LoyaltyPurchasePointsPanel(props: Props) {
  const { onContinue } = props
  const intl = useIntl()
  const dispatch = useAppDispatch()
  const isLoggedIn = useAppSelector(selectLoggedIn)
  const account = useAppSelector(state => state.loyalty.account)
  const hasLoyaltyAccount = useAppSelector(getHasLoyaltyAccount)
  const isInLoyaltyRegion = useAppSelector(getIsCurrentRegionUserLoyaltyRegion)
  const loyaltyRegion = useAppSelector(getLoyaltyAccountRegion)
  const hasActiveMembership = hasLoyaltyAccount && !!loyaltyRegion
  const wrongRegion = isLoggedIn && hasActiveMembership && !isInLoyaltyRegion
  const needsEnroll = isLoggedIn && !hasActiveMembership && !wrongRegion
  const loyaltyCurrency = useMemo(() => {
    return config.regions.find(r => r.code === loyaltyRegion)?.currencyCode
  }, [loyaltyRegion])
  const handleSwitchRegion = useCallback(() => {
    if (!loyaltyRegion) return
    switchRegion(loyaltyRegion)
  }, [loyaltyRegion])
  const programName = getLoyaltyProgramDisplayName()
  const { offers, fetching, error, hasCustomerEligibility } = useLoyaltyPurchasePointsOffers()

  // Fetch once when logged in. Bypass fetchLoyaltyAccount dedup when a prior error
  // left us without account data (e.g. transient 401 before login completed).
  useEffect(() => {
    if (!isLoggedIn) return

    if (!hasLoyaltyAccount && account.error) {
      dispatch({
        type: API_CALL,
        api: FETCH_LOYALTY_ACCOUNT,
        request: () => requestLoyaltyAccount(),
      })
      return
    }

    dispatch(fetchLoyaltyAccount())
  // oxlint-disable-next-line react-hooks/exhaustive-deps -- fetch on login only; avoid refetch loops
  }, [dispatch, isLoggedIn])

  const [selectedOfferId, setSelectedOfferId] = useState<string | undefined>()
  const [offerIdParam] = useQueryState('offerId', parseAsString)

  useEffect(() => {
    if (selectedOfferId || offers.length === 0) return
    const matched = offerIdParam ? offers.find(o => o.id === offerIdParam) : undefined
    const preselected = matched ? loyaltyPointsPurchasePickBestOffer(offers, matched.totalPoints) : undefined
    setSelectedOfferId((preselected ?? offers[0]).id)
  }, [offers, selectedOfferId, offerIdParam])

  const onOfferSelect = useCallback((offerId: string) => {
    setSelectedOfferId(offerId)
  }, [])

  const currentBalance = isLoggedIn ? account.data?.points : undefined

  const selectedOffer = useMemo(
    () => offers.find(offer => offer.id === selectedOfferId),
    [offers, selectedOfferId],
  )

  const isCapReached = !!(hasCustomerEligibility && selectedOffer?.isEligibleNow === false)

  const handleContinueClick = useCallback(() => {
    if (selectedOfferId) onContinue(selectedOfferId)
  }, [selectedOfferId, onContinue])

  const title = intl.formatMessage({
    id: 'XXGTI18CNoE_QSbYW0JB',
    defaultMessage: 'Purchase Société Points',
    description: 'Title of the right-column purchase panel on the Buy Points landing page',
  })

  const continueLabel = intl.formatMessage({
    id: 'BcT-t-zzEU7WJEuJwi8V',
    defaultMessage: 'Continue to Payment',
    description: 'Primary CTA on the Buy Points landing page panel that takes the member to checkout',
  })

  const errorLabel = intl.formatMessage({
    id: 'ssh84PFGIIgQYE7-93pW',
    defaultMessage: 'We couldn’t load offers right now. Please try again later.',
    description: 'Error message shown when the Buy Points offers endpoint fails',
  })

  const emptyLabel = intl.formatMessage({
    id: '1TykklMpCF4CX_Ob6Kzk',
    defaultMessage: 'Topping up points is not available in your region right now.',
    description: 'Message shown on the top-up-points landing page when no offers exist for the visitor’s currency',
  })

  return <Pane borderRadius="L">
    <PaneInner>
      <Group direction="vertical" gap={4}>
        <BodyText variant="medium" weight="bold">{title}</BodyText>

        {error && <Heading variant="heading6">{errorLabel}</Heading>}

        {!error && fetching && offers.length === 0 && <>
          <LoadingBox height={56} borderRadius="S" />
          {!isLoggedIn && <LoadingBox height={48} borderRadius="S" />}
          {isLoggedIn && <>
            <LoadingBox height={120} borderRadius="S" />
            <LoadingBox height={48} borderRadius="S" />
          </>}
        </>}

        {!error && !fetching && offers.length === 0 && (
          <Heading variant="heading6">{emptyLabel}</Heading>
        )}

        {!error && offers.length > 0 && <>
          <LoyaltyPurchasePointsOfferSelect
            offers={offers}
            selectedOfferId={selectedOfferId}
            onSelect={onOfferSelect}
            disabled={fetching || isCapReached}
            label=""
          />

          {!isLoggedIn && <LoyaltyPurchasePointsLoggedOutCta />}

          {needsEnroll && <LoyaltyPurchasePointsNeedsMembershipCta />}

          {isLoggedIn && hasActiveMembership && isCapReached && (
            <LoyaltyPurchasePointsCapReachedBanner
              nextEligiblePurchaseDate={selectedOffer?.nextEligiblePurchaseDate}
            />
          )}

          {isLoggedIn && hasActiveMembership && !isCapReached && (
            <Group direction="vertical" gap={24}>
              {selectedOffer && currentBalance !== undefined && (
                <LoyaltyPurchasePointsBreakdown
                  currentBalance={currentBalance}
                  offer={selectedOffer}
                />
              )}
              {!isInLoyaltyRegion && loyaltyCurrency && (
                <CSSBreakpoint max="tablet">
                  <BodyText variant="medium">
                    <FormattedMessage
                      id="fVLuF7ZKMQCgk9NCv_Nj"
                      defaultMessage="To purchase {programName} Points <link>Switch to {loyaltyCurrency} <icon></icon></link>"
                      description="Prompt on the Buy Points landing page to switch region to the loyalty account currency before purchasing points"
                      values={{
                        programName,
                        loyaltyCurrency,
                        link: (chunks: ReactNode) => <TextLink onClick={handleSwitchRegion}>{chunks}</TextLink>,
                        icon: () => <LineSyncIcon />,
                      }}
                    />
                  </BodyText>
                </CSSBreakpoint>
              )}
              <TextButton
                kind="primary"
                size="large"
                onClick={handleContinueClick}
                disabled={!selectedOfferId || fetching || !isInLoyaltyRegion}
              >
                {continueLabel}
              </TextButton>
            </Group>
          )}
        </>}
      </Group>
    </PaneInner>
  </Pane>
}

export default LoyaltyPurchasePointsPanel
